import { createClient } from '@/lib/supabase/client';

const EDGE_FUNCTION_URL = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-email`;

async function sendEmail(payload: {
  to: string | string[];
  type: string;
  subject?: string;
  data?: Record<string, unknown>;
}): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : {}),
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    if (!response.ok) {
      console.error('[EmailService] Failed to send email:', result);
      return { success: false, error: result.error || 'Unknown error' };
    }
    return { success: true, id: result.id };
  } catch (err: any) {
    console.error('[EmailService] Error:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Send a confirmation email to the contributor after they submit an item.
 */
export async function sendContributorSubmissionEmail(params: {
  to: string;
  contributorName: string;
  itemTitle: string;
  queueRef: string;
  lodgeName: string;
  fileCount: number;
}) {
  return sendEmail({
    to: params.to,
    type: 'contributor_submission',
    data: {
      contributorName: params.contributorName,
      itemTitle: params.itemTitle,
      queueRef: params.queueRef,
      lodgeName: params.lodgeName,
      fileCount: params.fileCount,
    },
  });
}

/**
 * Send the contributor token email to the recipient with their upload link.
 */
export async function sendTokenGeneratedEmail(params: {
  to: string;
  recipientName: string;
  tokenId: string;
  lodgeName: string;
  expiresAt: string;
  usesAllowed: number;
}) {
  return sendEmail({
    to: params.to,
    type: 'token_generated',
    data: {
      recipientName: params.recipientName,
      tokenId: params.tokenId,
      lodgeName: params.lodgeName,
      expiresAt: params.expiresAt,
      usesAllowed: params.usesAllowed,
      uploadUrl: `${process.env.NEXT_PUBLIC_SITE_URL}/contributor-upload-page`,
    },
  });
}

/**
 * Send a pending review alert to the lodge secretary/admin.
 */
export async function sendPendingReviewAlertEmail(params: {
  to: string;
  recipientName: string;
  lodgeName: string;
  pendingCount: number;
  flaggedCount: number;
  items?: Array<{ title: string; uploadedAt: string; status: string }>;
}) {
  return sendEmail({
    to: params.to,
    type: 'pending_review_alert',
    data: {
      recipientName: params.recipientName,
      lodgeName: params.lodgeName,
      pendingCount: params.pendingCount,
      flaggedCount: params.flaggedCount,
      items: params.items || [],
    },
  });
}

/**
 * Send an archive milestone celebration email.
 */
export async function sendArchiveMilestoneEmail(params: {
  to: string;
  recipientName: string;
  lodgeName: string;
  milestone: number;
  totalRecords: number;
  archivedCount: number;
}) {
  return sendEmail({
    to: params.to,
    type: 'archive_milestone',
    data: {
      recipientName: params.recipientName,
      lodgeName: params.lodgeName,
      milestone: params.milestone,
      totalRecords: params.totalRecords,
      archivedCount: params.archivedCount,
    },
  });
}

/**
 * Send an invitation email to a new user.
 */
export async function sendInvitationEmail(params: {
  to: string;
  inviterName: string;
  lodgeName: string;
  role: string;
  acceptUrl: string;
}) {
  return sendEmail({
    to: params.to,
    type: 'user_invitation',
    data: {
      inviterName: params.inviterName,
      lodgeName: params.lodgeName,
      role: params.role,
      acceptUrl: params.acceptUrl,
    },
  });
}
